import { browser } from '$app/environment';
import { derived, writable, type Writable } from 'svelte/store';

const SEPARATOR = '||';

const createStore = <T extends string | number>(key: string, fallbackValue: T): Writable<T> => {
	let initialValue: string | number;
	let searchParams;

	if (browser) {
		searchParams = new URLSearchParams(window.location.search);

		if (searchParams.has(key)) {
			if (typeof fallbackValue === 'string') {
				initialValue = searchParams.get(key);
			}
			if (typeof fallbackValue === 'number') {
				initialValue = parseInt(searchParams.get(key), 10);
			}
		} else {
			initialValue = fallbackValue;
		}
	} else {
		initialValue = fallbackValue;
	}

	const { set, update, subscribe } = writable(initialValue as T);

	return {
		set,
		update,
		subscribe,
	};
};

const createArrayStore = (key: string) => {
	let objectSet: Set<string> = new Set();
	let initialValue: string[];
	let searchParams;

	if (browser) {
		searchParams = new URLSearchParams(window.location.search);

		if (searchParams.has(key)) {
			initialValue = searchParams.get(key).split(SEPARATOR);
		} else {
			initialValue = [];
		}
	} else {
		initialValue = [];
	}

	const { set, update, subscribe } = writable(initialValue);

	return {
		set: (value: string[]) => {
			objectSet = new Set();
			value.forEach((id) => objectSet.add(id));

			set(value);
		},
		update,
		subscribe,
		remove: (id: string) => {
			if (objectSet.has(id)) {
				objectSet.delete(id);
			}
			const objectArray = [...objectSet];

			set(objectArray);
		},
	};
};

export const query = createStore('query', '');
export const filteredCookbooks = createArrayStore('cookbooks');
export const filteredIngredients = createArrayStore('ingredients');
export const filteredCategories = createArrayStore('categories');
export const filteredTask: Writable<Task | ''> = createStore('task', '');
export const currentIndex: Writable<'objects' | 'recipes'> = createStore('index', 'objects');
export const filterIsVisible = writable(false);

export const searchParams = derived(
	[query, filteredCookbooks, filteredIngredients, filteredCategories, filteredTask, currentIndex],
	([
		query,
		filteredCookbooks,
		filteredIngredients,
		filteredCategories,
		filteredTask,
		currentIndex,
	]) => {
		const record = [
			['query', query + ''],
			['cookbooks', filteredCookbooks.join(SEPARATOR)],
			['ingredients', filteredIngredients.join(SEPARATOR)],
			['categories', filteredCategories.join(SEPARATOR)],
			['task', filteredTask + ''],
			['index', currentIndex + ''],
		].filter(([key, value]) => value);

		return new URLSearchParams(record);
	}
);
